import React, { FC, useEffect, useMemo, useState } from 'react'
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride'

import WalkthroughItem from '@components/molecules/WalkthroughItem'
import { darkGreen } from '@constants/colors'
import { useGetStartedCheckList } from '@contexts/getStartedCheckListProvider'
import { IWalkthrough, WalkthroughType } from '@interfaces/components/organisms/Walkthrough'
import { getWalkthroughViewed, setWalkthroughViewed } from '@utils/localStorage'

const Walkthrough: FC<IWalkthrough> = ({ type, customOnNexClick }) => {
  const { selectedCheckList, setSelectedCheckList } = useGetStartedCheckList()

  const [run, setRun] = useState(false)

  useEffect(() => {
    const viewed = getWalkthroughViewed(type)
    if (type === WalkthroughType.PatientList || type === WalkthroughType.referralProgram) {
      if (!viewed) {
        setTimeout(() => setRun(true), 500)
      }
    } else if (selectedCheckList) {
      setTimeout(() => setRun(true), 500)
    }
  }, [type, selectedCheckList])

  const metaData: { steps: Step[]; closeButtonLabel: string } | null = useMemo(() => {
    if (type === WalkthroughType.PatientList && !selectedCheckList) {
      return {
        steps: [
          {
            disableBeacon: true,
            target: '.PTWalkthroughStep1',
            title: 'Test Clients',
            content: 'We’ve added three test clients to help you explore the platform.',
          },
        ],
        closeButtonLabel: 'Done',
      }
    }
    if (type === WalkthroughType.referralProgram && !selectedCheckList) {
      return {
        steps: [
          {
            disableBeacon: true,
            target: '.ASWalkthroughStep1',
            title: 'Referral Program',
            content: 'Invite a friend to uMore and you’ll both get 1 month free!',
            placement: 'left',
          },
        ],
        closeButtonLabel: 'OK, thanks!',
      }
    }
    if (type === WalkthroughType.GenerateAssignment && selectedCheckList === 'generateAssignment') {
      return {
        steps: [
          {
            disableBeacon: true,
            target: '.FGWalkthroughStep1',
            title: 'Generate your own resources',
            content:
              'Choose what type of resource you want to generate. For now, only worksheet and assessments support this feature.',
          },
          {
            disableBeacon: true,
            target: '.FGWalkthroughStep1',
            title: 'Generate your own resources',
            content:
              'Describe what you want to generate in the input box and then click the "Generate" button',
          },
        ],
        closeButtonLabel: 'OK, thanks!',
      }
    }
    if (
      type === WalkthroughType.AIEditorFirstStep &&
      selectedCheckList === 'editAssignmentWithAI'
    ) {
      return {
        steps: [
          {
            disableBeacon: true,
            target: '.CMBWalkthroughStep1',
            title: 'Edit assignment with AI',
            content: 'select an assignment to get started.',
          },
        ],
        closeButtonLabel: 'OK',
      }
    }
    if (type === WalkthroughType.AIEditor && selectedCheckList === 'editAssignmentWithAI') {
      return {
        steps: [
          {
            disableBeacon: true,
            target: '.CMBWalkthroughStep2',
            title: 'Edit assignment with AI',
            content:
              'Use our AI Editor to modify assignments further.\n\nJust type in what you want to change.',
          },
        ],
        closeButtonLabel: 'OK, thanks!',
      }
    }
    if (
      type === WalkthroughType.checkClientProgress &&
      selectedCheckList === 'checkClientProgress'
    ) {
      return {
        steps: [
          {
            disableBeacon: true,
            target: '.PATSelectAssignmentWalkthroughStep1',
            content:
              "Select the assignment from the dropdown so you can track client's progress over time.",
          },
        ],
        closeButtonLabel: 'OK, thanks!',
      }
    }
    return null
  }, [selectedCheckList, type])

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

    if (finishedStatuses.includes(status)) {
      setWalkthroughViewed(type)
      if (type !== WalkthroughType.AIEditorFirstStep) {
        setSelectedCheckList(undefined)
      }
    }
  }

  if (!metaData || !run) return null
  return (
    <Joyride
      disableScrollParentFix
      scrollOffset={200}
      disableOverlay
      run={run}
      steps={metaData.steps}
      spotlightPadding={-5}
      showProgress={metaData.steps.length > 1}
      callback={handleJoyrideCallback}
      tooltipComponent={({ step, index, backProps, primaryProps }) => {
        return (
          <WalkthroughItem
            index={index}
            total={metaData.steps?.length}
            content={step.content}
            title={step.title}
            closeLabel={metaData.closeButtonLabel}
            onNextClick={(event) => {
              const customFunction = customOnNexClick?.[index]
              if (customFunction) {
                customFunction()
              }
              setTimeout(() => primaryProps.onClick(event), 300)
            }}
            onBackClick={backProps.onClick}
          />
        )
      }}
      styles={{
        options: {
          arrowColor: darkGreen,
          zIndex: 1000,
        },
      }}
    />
  )
}

export default Walkthrough
